/**
 * BasketballWireReport - The Wire Report for Basketball
 * Single-video analysis page with frame-by-frame controls, markers, chat, and multilingual support
 *
 * UX Flow: Video-First → User Triggers Analysis → Matrix Animation (15s) → Report Reveal
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share2,
  Maximize2,
  Settings,
  List,
  MessageSquare,
  Info,
  Calendar,
  Trophy,
  ChevronRight,
  Play,
  Sparkles,
  Zap,
} from 'lucide-react';

// Import components
import BasketballVideoPlayer from './BasketballVideoPlayer';
import MarkerOverlay from './MarkerOverlay';
import VideoChat from './VideoChat';
import GameDataPanel from './GameDataPanel';
import LanguageSelector from './LanguageSelector';
import MatrixBackground from './MatrixBackground';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';

// Import game data
import {
  gameMetadata,
  keyMoments,
  playerAnalysis,
  tacticalBreakdown,
  benchDynamics,
  recommendations,
  translations as allTranslations,
} from '../../data/basketball/elClasico2026';

// Analysis state enum
const ANALYSIS_STATES = {
  VIDEO_READY: 'video-ready',    // Initial: Video visible, waiting for user
  ANALYZING: 'analyzing',         // Matrix animation playing
  COMPLETE: 'complete',           // Report revealed
};

// Analyze Button Component
const AnalyzeButton = ({ onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="group relative w-full max-w-md mx-auto flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl shadow-lg shadow-yellow-400/25 hover:shadow-yellow-400/40 hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
    <span>Analyze with SLAM AI</span>
    <Zap className="w-5 h-5 group-hover:animate-bounce" />
  </button>
);

// Basketball Icon SVG
const BasketballIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2.5" fill="none" />
    <line x1="4" y1="32" x2="60" y2="32" stroke="currentColor" strokeWidth="2" />
    <line x1="32" y1="4" x2="32" y2="60" stroke="currentColor" strokeWidth="2" />
    <path d="M32 4 Q14 18, 14 32 Q14 46, 32 60" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M32 4 Q50 18, 50 32 Q50 46, 32 60" stroke="currentColor" strokeWidth="2" fill="none" />
  </svg>
);

// Analysis Progress Overlay (shown during Matrix animation)
const AnalysisProgress = ({ progress, onSkip }) => {
  const phases = [
    { text: 'Analyzing Video Frames...', range: [0, 33] },
    { text: 'Processing Biomechanics...', range: [33, 66] },
    { text: 'Generating Intelligence Report...', range: [66, 100] },
  ];

  const currentPhase = phases.find(p => progress >= p.range[0] && progress < p.range[1]) || phases[2];

  // Circle progress calculation using strokeDashoffset
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center cursor-pointer"
      onClick={onSkip}
    >
      {/* Click outside hint */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 text-sm animate-pulse">
        Click anywhere to skip
      </div>

      {/* Card - stop propagation so clicking inside doesn't skip */}
      <div
        className="bg-black/80 backdrop-blur-xl border border-white/[0.12] rounded-2xl p-8 max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress Circle with Basketball */}
        <div className="relative w-44 h-44 mx-auto mb-6">
          <svg className="w-full h-full" viewBox="0 0 176 176">
            {/* Background circle */}
            <circle
              cx="88"
              cy="88"
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="6"
            />
            {/* Progress circle - animated glow */}
            <circle
              cx="88"
              cy="88"
              r={radius}
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '88px 88px',
                transition: 'stroke-dashoffset 0.3s ease',
                filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.6))'
              }}
            />
            {/* Gradient definition */}
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="100%" stopColor="#FF8C00" />
              </linearGradient>
            </defs>
          </svg>

          {/* Center content: Basketball + Percentage */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <BasketballIcon className="w-12 h-12 text-orange-500 mb-1" />
            <span className="text-yellow-400 text-2xl font-bold" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.5)' }}>
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {/* Phase Text */}
        <p className="text-white text-center font-medium mb-2">{currentPhase.text}</p>
        <p className="text-white/50 text-center text-sm">SLAM AI is analyzing the game footage</p>
      </div>
    </div>
  );
};

// Report Reveal Wrapper (animated container)
const ReportReveal = ({ children, delay = 0, visible }) => (
  <div
    className={`transition-all duration-700 ease-out ${
      visible
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 translate-y-8 pointer-events-none'
    }`}
    style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
  >
    {children}
  </div>
);

// Key Moments Timeline component
const KeyMomentsTimeline = ({ moments, currentMoment, onMomentClick, translations }) => {
  const timelineRef = useRef(null);

  // Scroll timeline horizontally to current moment (without page scroll)
  useEffect(() => {
    if (currentMoment && timelineRef.current) {
      const element = timelineRef.current.querySelector(`[data-moment-id="${currentMoment.id}"]`);
      if (element) {
        // Only scroll within the timeline container, not the page
        const container = timelineRef.current;
        const elementLeft = element.offsetLeft;
        const containerWidth = container.clientWidth;
        const elementWidth = element.offsetWidth;
        const scrollTarget = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        container.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }
  }, [currentMoment?.id]);

  const categoryColors = {
    clutch: 'bg-yellow-500',
    shooting: 'bg-green-500',
    drive: 'bg-blue-500',
    playmaking: 'bg-purple-500',
    defense: 'bg-red-500',
    rebounding: 'bg-orange-500',
    post: 'bg-pink-500',
    biomechanics: 'bg-cyan-500',
    fatigue: 'bg-amber-500',
    psychology: 'bg-indigo-500',
    officiating: 'bg-stone-500',
    dagger: 'bg-red-600',
  };

  return (
    <div className="glass-panel rounded-xl p-4">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <List className="w-4 h-4 text-yellow-400" />
        {translations.keyMoments || 'Key Moments'}
        <span className="ml-auto text-white/50 text-sm font-normal">
          {moments.length} events
        </span>
      </h3>
      <div
        ref={timelineRef}
        className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar"
      >
        {moments.map((moment) => {
          const isActive = currentMoment?.id === moment.id;
          return (
            <button
              key={moment.id}
              data-moment-id={moment.id}
              onClick={() => onMomentClick(moment)}
              className={`flex-shrink-0 w-48 p-3 rounded-lg border transition-all text-left backdrop-blur-sm ${
                isActive
                  ? 'bg-yellow-400/10 border-yellow-400'
                  : 'bg-white/[0.04] border-white/[0.15] hover:border-white/30 hover:bg-white/[0.08]'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${categoryColors[moment.category] || 'bg-gray-500'}`} />
                <span className="text-white/60 text-xs font-mono">{moment.timeVideo}</span>
                {moment.team && (
                  <span className={`ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded ${
                    moment.team === 'Real Madrid' ? 'bg-white/20 text-white' : 'bg-blue-600/50 text-white'
                  }`}>
                    {moment.team === 'Real Madrid' ? 'RMA' : 'BAR'}
                  </span>
                )}
              </div>
              <h4 className="text-white text-sm font-medium truncate">{moment.event}</h4>
              {moment.player && (
                <p className="text-white/50 text-xs mt-1 truncate">{moment.player}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Compact unified header - single bar with all info
const GameHeader = ({ metadata, translations, language, onLanguageChange, onBack }) => {
  return (
    <div className="bg-black/95 backdrop-blur-xl border-b border-white/[0.15] sticky top-0 z-30 shrink-0">
      <div className="w-full px-3 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back + Badge */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={onBack}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>
            <span className="px-2 py-0.5 bg-yellow-400 text-black text-[10px] font-bold rounded">
              {translations.title || 'THE WIRE'}
            </span>
          </div>

          {/* Center: Score (inline) */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                <span className="text-black font-bold text-[8px]">RMA</span>
              </div>
              <span className="text-white/80 text-sm font-medium hidden sm:inline">Real Madrid</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-white/[0.08] rounded-lg">
              <span className="text-xl font-bold text-white tabular-nums">98</span>
              <span className="text-white/40 text-sm">-</span>
              <span className="text-xl font-bold text-white tabular-nums">105</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/80 text-sm font-medium hidden sm:inline">FC Barcelona</span>
              <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                <span className="text-white font-bold text-[8px]">BAR</span>
              </div>
            </div>
          </div>

          {/* Right: Metadata + Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden lg:flex items-center gap-3 text-white/50 text-xs mr-2">
              <span className="flex items-center gap-1">
                <Trophy className="w-3 h-3" />
                {metadata.competition}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {metadata.date}
              </span>
            </div>
            <LanguageSelector
              currentLanguage={language}
              onLanguageChange={onLanguageChange}
              compact
            />
            <button className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Share">
              <Share2 className="w-4 h-4 text-white/60" />
            </button>
            <button className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Download">
              <Download className="w-4 h-4 text-white/60" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component
const BasketballWireReport = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState('en');
  const [currentTime, setCurrentTime] = useState(0);
  const [currentMarker, setCurrentMarker] = useState(null);
  const [showMarkerOverlay, setShowMarkerOverlay] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [view, setView] = useState('full'); // 'full', 'video-focus', 'data-focus'
  const videoPlayerRef = useRef(null);

  // New staged analysis state
  const [analysisState, setAnalysisState] = useState(ANALYSIS_STATES.VIDEO_READY);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const analysisTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const translations = allTranslations[language] || allTranslations.en;

  // Derived state
  const isAnalyzing = analysisState === ANALYSIS_STATES.ANALYZING;
  const isReportVisible = analysisState === ANALYSIS_STATES.COMPLETE;

  // Handle time update from video player
  const handleTimeUpdate = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  // Handle marker hit (auto-popup when timeline passes marker)
  const handleMarkerHit = useCallback((marker) => {
    // Only show markers after analysis is complete
    if (analysisState === ANALYSIS_STATES.COMPLETE) {
      setCurrentMarker(marker);
      setShowMarkerOverlay(true);
    }
  }, [analysisState]);

  // Handle marker click from timeline
  const handleMarkerClick = useCallback((marker) => {
    setCurrentMarker(marker);
    setShowMarkerOverlay(true);
  }, []);

  // Handle overlay dismiss
  const handleOverlayDismiss = useCallback(() => {
    setShowMarkerOverlay(false);
    setCurrentMarker(null);
  }, []);

  // Handle overlay resume (continue playback)
  const handleOverlayResume = useCallback(() => {
    setShowMarkerOverlay(false);
    // Video continues playing
  }, []);

  // Start analysis when user clicks "Analyze" button
  const handleStartAnalysis = useCallback(() => {
    setAnalysisState(ANALYSIS_STATES.ANALYZING);
    setAnalysisProgress(0);

    // Progress animation over 15 seconds
    const duration = 15000;
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setAnalysisProgress(progress);

      if (progress < 100) {
        analysisTimerRef.current = requestAnimationFrame(updateProgress);
      } else {
        // Analysis complete
        setAnalysisState(ANALYSIS_STATES.COMPLETE);
      }
    };

    analysisTimerRef.current = requestAnimationFrame(updateProgress);
  }, []);

  // Skip analysis and show results immediately
  const handleSkipAnalysis = useCallback(() => {
    if (analysisTimerRef.current) {
      cancelAnimationFrame(analysisTimerRef.current);
    }
    setAnalysisProgress(100);
    setAnalysisState(ANALYSIS_STATES.COMPLETE);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (analysisTimerRef.current) {
        cancelAnimationFrame(analysisTimerRef.current);
      }
    };
  }, []);

  // Scroll to top when analysis completes
  useEffect(() => {
    if (analysisState === ANALYSIS_STATES.COMPLETE) {
      // Scroll both window and container to top
      window.scrollTo({ top: 0, behavior: 'instant' });
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [analysisState]);

  // Video and poster URLs for El Clasico analysis
  // Barcelona @ Real Madrid - Jan 4, 2026 (EuroLeague El Clasico)
  const videoUrl = "https://storage.googleapis.com/slamsportsai.appspot.com/demos/el-clasico-2026-highlights.mp4";
  // Use a data URI placeholder for the poster to avoid 404 errors
  const posterUrl = null; // Will show black background with play button

  // Reset scroll position on mount to prevent layout issues
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <div className="flex flex-col h-full text-white bg-black">
      {/* Matrix animation during analysis */}
      <MatrixBackground isActive={isAnalyzing} phase="analyzing" />

      {/* Analysis Progress Overlay (during Matrix animation) */}
      {isAnalyzing && (
        <AnalysisProgress
          progress={analysisProgress}
          onSkip={handleSkipAnalysis}
        />
      )}

      {/* Header */}
      <GameHeader
        metadata={gameMetadata}
        translations={translations}
        language={language}
        onLanguageChange={setLanguage}
        onBack={() => navigate(-1)}
      />

      {/* Main content - flex-1 to fill available space */}
      <div ref={scrollContainerRef} className="flex-1 overflow-hidden">
        {/* Pre-analysis: Centered video with analyze button */}
        {!isReportVisible && (
          <div className="h-full overflow-y-auto px-4 md:px-6 py-4 md:py-6">
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                <BasketballVideoPlayer
                  videoUrl={videoUrl}
                  posterUrl={posterUrl}
                  markers={[]}
                  currentMarker={currentMarker}
                  onTimeUpdate={handleTimeUpdate}
                  onMarkerHit={handleMarkerHit}
                  onMarkerClick={handleMarkerClick}
                  isAnalyzing={isAnalyzing}
                  translations={translations}
                />
              </div>
              {analysisState === ANALYSIS_STATES.VIDEO_READY && (
                <div className="py-6">
                  <p className="text-center text-white/60 mb-4">
                    Watch the video, then click below to generate the AI analysis report
                  </p>
                  <AnalyzeButton onClick={handleStartAnalysis} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post-analysis: Resizable panels layout */}
        {isReportVisible && (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Left Panel: Video + Timeline + Chat */}
            <ResizablePanel defaultSize={55} minSize={35} maxSize={75}>
              <div className="h-full overflow-y-auto p-4 space-y-4">
                {/* Compact video player */}
                <div className="relative">
                  <BasketballVideoPlayer
                    videoUrl={videoUrl}
                    posterUrl={posterUrl}
                    markers={keyMoments}
                    currentMarker={currentMarker}
                    onTimeUpdate={handleTimeUpdate}
                    onMarkerHit={handleMarkerHit}
                    onMarkerClick={handleMarkerClick}
                    isAnalyzing={isAnalyzing}
                    translations={translations}
                  />
                  {showMarkerOverlay && (
                    <MarkerOverlay
                      marker={currentMarker}
                      onDismiss={handleOverlayDismiss}
                      onResume={handleOverlayResume}
                      position="top-left"
                      autoCollapseAfter={0}
                    />
                  )}
                </div>

                {/* Key moments timeline */}
                <ReportReveal visible={isReportVisible} delay={200}>
                  <KeyMomentsTimeline
                    moments={keyMoments}
                    currentMoment={currentMarker}
                    onMomentClick={handleMarkerClick}
                    translations={translations}
                  />
                </ReportReveal>

                {/* Chat interface - desktop */}
                <ReportReveal visible={isReportVisible} delay={800}>
                  <div className="hidden lg:block">
                    <VideoChat
                      gameData={{ gameMetadata, keyMoments, playerAnalysis, tacticalBreakdown }}
                      currentMarker={currentMarker}
                      translations={translations}
                      isMinimized={chatMinimized}
                      onToggleMinimize={() => setChatMinimized(!chatMinimized)}
                    />
                  </div>
                </ReportReveal>
              </div>
            </ResizablePanel>

            {/* Resize Handle */}
            <ResizableHandle className="w-1 bg-white/[0.08] hover:bg-yellow-500/30 transition-colors data-[resize-handle-active]:bg-yellow-500/50" />

            {/* Right Panel: Game Data */}
            <ResizablePanel defaultSize={45} minSize={25} maxSize={65}>
              <div className="h-full overflow-y-auto p-4 custom-scrollbar">
                <ReportReveal visible={isReportVisible} delay={400}>
                  <GameDataPanel
                    playerAnalysis={playerAnalysis}
                    tacticalBreakdown={tacticalBreakdown}
                    recommendations={recommendations}
                    benchDynamics={benchDynamics}
                    translations={translations}
                  />
                </ReportReveal>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}

        {/* Mobile chat (revealed after analysis) */}
        {isReportVisible && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20">
            <VideoChat
              gameData={{ gameMetadata, keyMoments, playerAnalysis, tacticalBreakdown }}
              currentMarker={currentMarker}
              translations={translations}
              isMinimized={chatMinimized}
              onToggleMinimize={() => setChatMinimized(!chatMinimized)}
            />
          </div>
        )}
      </div>

      {/* Footer - use mt-auto to push to bottom in flex container */}
      <footer className="border-t border-white/[0.15] py-4 mt-auto shrink-0">
        <div className="w-full px-4 flex items-center justify-between text-white/40 text-xs">
          <span>SLAM Sports AI · The Wire Report</span>
          <span>Analysis scope: {gameMetadata.analysisScope}</span>
        </div>
      </footer>
    </div>
  );
};

export default BasketballWireReport;
